/**
 * Member-grid renderer — sortable, clickable table of member rows.
 *
 * Each column declares its render type: text / number / badge / score_bar
 * / date. Clicking a header sorts; clicking a row navigates to that
 * member's intel page (workspace_id query string preserved).
 *
 * The columns array is AI-defined — the AI assembles whatever rows
 * make sense for the current question (alignment scores, relationship
 * tiers, last touch dates, cosponsor status…) and the grid renders
 * them with type-aware formatting.
 *
 * Pure React. Sort + click state lives inside the component, so undoing or replaying
 * a message doesn't accidentally lock in a stale sort.
 */

import { useState } from "react";
import { registerRenderer, type RendererProps } from "./registry.js";
import { ChevronDown, ChevronUp } from "lucide-react";

export type MemberGridColumnType =
  | "text"
  | "number"
  | "badge"
  | "score_bar"
  | "date"
  | "photo";

export interface MemberGridColumn {
  /** Property name to read from each row. */
  key: string;
  /** Header label. Falls back to `key` if omitted. */
  label?: string;
  /** Cell render type. Defaults to "text". */
  type?: MemberGridColumnType;
}

export interface MemberGridRow {
  /** Bioguide id for row-click navigation. */
  bioguide_id?: string;
  /** Alternative id field if bioguide isn't set. */
  id?: string;
  /** Arbitrary extra fields keyed by column.key. */
  [k: string]: unknown;
}

export interface MemberGridInput {
  title?: string;
  members: MemberGridRow[];
  columns: MemberGridColumn[];
  /** Initial sort column key. Defaults to first column. */
  sort_key?: string;
  /** Initial sort direction. Defaults to "desc". */
  sort_dir?: "asc" | "desc";
}

const DEFAULT_COLUMNS: MemberGridColumn[] = [
  { key: "display_name", label: "Member" },
  { key: "party", label: "Party", type: "badge" },
  { key: "state", label: "State" },
];

function partyBadgeClass(v: string): string {
  if (v.startsWith("R")) return "bg-red-100 text-red-800";
  if (v.startsWith("D")) return "bg-blue-100 text-blue-800";
  return "bg-amber-100 text-amber-800";
}

function memberInitials(name: string): string {
  const parts = name.replace(/^Sen\.\s*|^Rep\.\s*|^Dr\.\s*/, "").trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Cell({
  row,
  col,
}: {
  row: MemberGridRow;
  col: MemberGridColumn;
}) {
  const v = row[col.key];
  const t = col.type ?? "text";
  // Photo cells render even when v is empty — they pull from row.bioguide_id.
  if (t === "photo") {
    const bio = String(row.bioguide_id ?? row.id ?? "");
    const name = String(row.display_name ?? row.name ?? "?");
    const initials = memberInitials(name);
    return (
      <td className="px-2.5 py-1.5 text-xs w-9">
        <div className="w-7 h-7 rounded-full bg-stone-200 overflow-hidden relative flex-shrink-0">
          {bio ? (
            <img
              src={`/api/v1/photos/members/${encodeURIComponent(bio)}.jpg`}
              alt={name}
              loading="lazy"
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : null}
          <div className="absolute inset-0 flex items-center justify-center text-stone-500 text-[9px] font-bold tracking-wider pointer-events-none">
            {initials}
          </div>
        </div>
      </td>
    );
  }
  if (v == null || v === "") {
    return <td className="px-2.5 py-1.5 text-xs"></td>;
  }
  if (t === "badge") {
    return (
      <td className="px-2.5 py-1.5 text-xs">
        <span
          className={`inline-block px-1.5 py-px rounded text-[10px] font-semibold ${partyBadgeClass(String(v))}`}
        >
          {String(v)}
        </span>
      </td>
    );
  }
  if (t === "score_bar") {
    const n = Math.max(0, Math.min(100, Number(v)));
    return (
      <td className="px-2.5 py-1.5 text-xs whitespace-nowrap">
        <div className="inline-block w-[60px] h-[6px] bg-stone-200 rounded overflow-hidden align-middle mr-1.5">
          <div
            className="h-full rounded"
            style={{
              width: `${n}%`,
              background: "var(--color-action)",
            }}
          />
        </div>
        <span className="font-mono text-[10px] tabular-nums">{n.toFixed(0)}</span>
      </td>
    );
  }
  if (t === "date") {
    const d = new Date(v as string);
    return (
      <td className="px-2.5 py-1.5 text-xs">
        {isNaN(d.getTime()) ? String(v) : d.toLocaleDateString()}
      </td>
    );
  }
  if (t === "number") {
    const n = Number(v);
    return (
      <td className="px-2.5 py-1.5 text-xs text-right font-mono tabular-nums">
        {isNaN(n) ? String(v) : n.toLocaleString()}
      </td>
    );
  }
  return <td className="px-2.5 py-1.5 text-xs">{String(v)}</td>;
}

function MemberGridCard({ input }: RendererProps<MemberGridInput>) {
  const cols =
    Array.isArray(input.columns) && input.columns.length > 0
      ? input.columns
      : DEFAULT_COLUMNS;
  const [sortKey, setSortKey] = useState(input.sort_key || cols[0].key);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    input.sort_dir === "asc" ? "asc" : "desc"
  );

  const members = (Array.isArray(input.members) ? input.members.slice() : []).sort(
    (a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "number" && typeof bv === "number") {
        return sortDir === "asc" ? av - bv : bv - av;
      }
      return sortDir === "asc"
        ? String(av ?? "").localeCompare(String(bv ?? ""))
        : String(bv ?? "").localeCompare(String(av ?? ""));
    }
  );

  const handleHeaderClick = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const handleRowClick = (row: MemberGridRow) => {
    const bio = row.bioguide_id || row.id;
    if (!bio) return;
    const wsId = new URLSearchParams(location.search).get("workspace_id");
    let url = `/members/${encodeURIComponent(String(bio))}`;
    if (wsId) url += `?workspace_id=${encodeURIComponent(wsId)}`;
    location.href = url;
  };

  return (
    <div
      className="self-stretch border border-stone-200 rounded-xl shadow-card bg-white py-2 flex flex-col gap-2"
    >
      {input.title && (
        <div className="px-3 text-sm font-semibold text-stone-900">
          {input.title}{" "}
          <span className="text-stone-400 font-normal">· {members.length}</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              {cols.map((c) => (
                <th
                  key={c.key}
                  className="text-left px-2.5 py-1.5 cursor-pointer text-stone-500 font-semibold uppercase text-[10px] border-b border-stone-200 whitespace-nowrap hover:text-stone-800"
                  onClick={() => handleHeaderClick(c.key)}
                >
                  {c.label || c.key}
                  {c.key === sortKey && (sortDir === "asc"
                    ? <ChevronUp size={11} className="inline ml-0.5 -mt-px" aria-hidden />
                    : <ChevronDown size={11} className="inline ml-0.5 -mt-px" aria-hidden />)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.map((row, i) => (
              <tr
                key={i}
                className="cursor-pointer transition-colors hover:bg-stone-50 border-b border-stone-100 last:border-b-0"
                onClick={() => handleRowClick(row)}
              >
                {cols.map((c) => (
                  <Cell key={c.key} row={row} col={c} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

registerRenderer<MemberGridInput>({
  tool: "render_member_grid",
  label: "Sortable, clickable member table",
  Component: MemberGridCard,
});

export { MemberGridCard };
